// @flow

import * as React from 'react';
import useSheet from 'app/jss';
import { every, range } from 'lodash';
import classNames from 'classnames';
import GridLayoutItem from './GridLayoutItem';
import AnimatedGroup from '../commons/AnimatedGroup';

type Props = {
  classes: Object,
  // The number of columns on dislay
  columns: number,
  gutter?: number,
  ratio?: { h: number, w: number },
  children?: any,
  onEnter?: () => void,
  onLeave?: () => void,
  onAppear?: () => void,
  onRef?: (node: any) => void,
  animationDelay?: number
};

const buildBasePositions = columns => {
  const val = {};
  range(0, columns).forEach(xVal => {
    val[xVal] = {};
  });
  return val;
};

function GridLayout(props: Props) {
  const {
    classes,
    columns,
    ratio = { h: 1, w: 1 },
    gutter = 20,
    children,
    onEnter,
    onLeave,
    onAppear,
    onRef,
    animationDelay = 0
  } = props;
  const firstRender = React.useRef(true);

  React.useEffect(() => {
    firstRender.current = false;
  }, []);

  // Rebuild occupancy each render so layout never accumulates stale positions.
  const positions = buildBasePositions(columns);

  const getPosition = (width, height) => {
    const findPos = (x, y) => {
      // check to see if the size of our item would fit
      // in the space given. We don't check the -entire- size
      // because we know that due to the order we process things
      // it's not possible to find something below us that, thus
      // the Y would never trigger and the X will check every
      // possible X value
      const withinColumns = x + width <= columns;
      const isInXBounds = withinColumns && every(range(x, x + width), xVal => !positions[xVal][y]);
      const isInYBounds = isInXBounds && every(range(y, y + height), yVal => !positions[x][yVal]);

      if (isInXBounds && isInYBounds) {
        return { x, y };
      }
      // need to move back to start if we're at the last column
      const nextX = (x + 1) % columns;
      // got to move to next row if the x moved to the next column
      const nextY = nextX < x ? y + 1 : y;
      return findPos(nextX, nextY);
    };

    const position = findPos(0, 0);
    // now fill in the positions array so the next value can get it right
    range(position.x, position.x + width).forEach(xVal => {
      range(position.y, position.y + height).forEach(yVal => {
        positions[xVal][yVal] = true;
      });
    });

    return position;
  };

  const processedChildren = React.Children.map(children, child => {
    if (!child) {
      return undefined;
    }
    if (child.type !== GridLayoutItem) {
      console.warn('child provided to grid layout without being wrapped in grid layout item');
      return undefined;
    }
    const childProps = { ...child.props };

    const position = getPosition(childProps.width, childProps.height);
    const translateX = `translateX(${(position.x * 100) / childProps.width}%)`;
    const translateY = `translateY(${(position.y * 100) / childProps.height}%)`;
    const style = {
      transform: `${translateX} ${translateY} translateZ(0)`,
      width: `${(100 / columns) * childProps.width}%`
    };
    const gutterStyle = {
      left: `${gutter}px`,
      top: `${gutter}px`
    };

    childProps.style = { ...childProps.style, ...style };
    childProps.gutterStyle = gutterStyle;
    childProps.ratio = childProps.ratio || ratio;

    return React.cloneElement(child, childProps);
  });
  const yBounds = Object.values(positions).map(xVals => Object.keys(xVals).length);
  const maximumYBound = Math.max(0, ...yBounds);
  const style = {
    paddingBottom: `${((ratio.h * maximumYBound) / (ratio.w * columns)) * 100}%`,
    marginLeft: `-${gutter}px`,
    marginTop: `-${gutter}px`
  };

  return (
    <div ref={onRef} style={{ marginRight: -gutter, transitionDelay: `${animationDelay}s` }}>
      <AnimatedGroup
        className={classNames(classes.gridLayout, { animate: !firstRender.current })}
        style={style}
        onEnter={onEnter}
        onLeave={onLeave}
        onAppear={onAppear}
      >
        {processedChildren}
      </AnimatedGroup>
    </div>
  );
}

function applyStyles() {
  return {
    gridLayout: {
      width: '100%',
      position: 'relative',
      transition: 'padding-bottom 0.3s ease-in-out',
      // Kick this into always rendering in the GPU
      // because this will almost always cause part pixel rendering
      // gpu rendering vs normal rendering seems to round part pixels
      // differently. So to keep it predictable, always keep it in GPU mode
      transform: 'translateZ(0)'
    }
  };
}

export default useSheet(applyStyles)(GridLayout);
